import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import { SmallArrowDownIcon, SearchIcon, EditIcon, DeleteIcon } from '../Icons'
import StatusToggle from '../ui/StatusToggle'
import ConfirmModal from '../ui/ConfirmModal'
import ScoreDisplay from '../ui/ScoreDisplay'
import { Plus } from 'lucide-react'
import Modal from '../ui/Modal'
import AddBatch from './AddBatch'
import EditBatch from './EditBatch'

function BatchList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesCount, setEntriesCount] = useState(200)
  const [currentPage, setCurrentPage] = useState(1)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalAction, setStatusModalAction] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [batchToDelete, setBatchToDelete] = useState(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedBatchId, setSelectedBatchId] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getAllBatches()
        setIsLoading(false)
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const getAllBatches = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}getallbatches`
      )
      if (response.status === 200) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error fetching batches:', error)
      toast.error('Failed to fetch batches')
    }
  }

  const deleteBatch = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}delete_batch/${id}`
      )
      toast.success(response.data)
      await getAllBatches()
    } catch (error) {
      toast.error('Failed to delete batch')
    }
  }

  const handleStatusToggle = (id, currentStatus) => {
    setStatusModalAction({ id, type: currentStatus ? 'block' : 'unblock' })
    setStatusModalOpen(true)
  }

  const handleDeleteClick = (id) => {
    setBatchToDelete(id)
    setDeleteModalOpen(true)
  }

  const blockBatch = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}block_batch/${id}`
      )
      toast.success(response.data)
      await getAllBatches()
    } catch (error) {
      toast.error('Failed to deactivate batch')
    }
  }

  const unblockBatch = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}unblock_batch/${id}`
      )
      toast.success(response.data)
      await getAllBatches()
    } catch (error) {
      toast.error('Failed to activate batch')
    }
  }

  // Filtered and paginated data
  const filteredData = data.filter((item) => {
    return (
      item.batch_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.batch_title?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  const indexOfLastEntry = currentPage * entriesCount
  const indexOfFirstEntry = indexOfLastEntry - entriesCount
  const paginatedData = filteredData.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(filteredData.length / entriesCount)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [entriesCount, searchTerm])

  const handleOpenAddModal = () => setAddModalOpen(true)
  const handleCloseAddModal = () => {
    setAddModalOpen(false)
    getAllBatches()
  }

  const handleOpenEditModal = (id) => {
    setSelectedBatchId(id)
    setEditModalOpen(true)
  }
  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setSelectedBatchId(null)
    getAllBatches()
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      {/* Header Controls */}
      <div className="justify-between items-center border border-[#F0F0F0] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg text-[#030303] font-bold">Batch List</h2>
        <div className="self-stretch flex min-w-60 items-center gap-2.5 text-xs text-[#535862] flex-wrap w-[750px] my-auto max-md:max-w-full">
          <div className="self-stretch flex items-center gap-[9px] my-auto">
            <div className="text-[#535862] self-stretch my-auto">
              Show Entries
            </div>
            <div className="self-stretch flex items-center gap-2 whitespace-nowrap text-center w-[90px] my-auto relative">
              <div className="justify-center items-stretch border border-[#F0F0F0] self-stretch flex w-[90px] flex-col bg-white my-auto px-2.5 py-[9px] rounded-[10px] border-solid relative">
                <select
                  className="text-[#535862] self-stretch my-auto bg-transparent outline-none appearance-none w-full pr-6"
                  value={entriesCount}
                  onChange={(e) => setEntriesCount(Number(e.target.value))}
                  aria-label="Entries per page"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  {[200, 400, 600].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex w-4 h-4">
                  <SmallArrowDownIcon />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch min-w-60 text-sm font-normal grow shrink w-[356px] my-auto rounded-[0px_0px_0px_0px]">
            <div className="flex items-center gap-1 border border-[#F0F0F0] bg-white px-3 py-2 rounded-md w-full focus-within:border-[#DE3C7D]">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search batch..."
                className="text-[#535862] bg-transparent outline-none border-none w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleOpenAddModal}
            className="justify-center items-stretch self-stretch flex flex-col text-white text-center grow shrink w-[150px] bg-[#DE3C7D] my-auto p-2.5 rounded-md flex-shrink-0"
          >
            <div className="flex w-full gap-1 items-center justify-center">
              <div className="text-white">Add Batch</div>
              <Plus className="w-4 h-4 text-white" />
            </div>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neutral-50">
            <tr>
              <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                S.No
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Batch Code
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Batch Schedule
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Batch Name
              </th>
              <th className="text-sm text-[#535862] font-medium text-center border w-40 px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Students
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-24 px-2 py-3.5 border-[#F0F0F0] batch-table-header"
                colSpan={2}
              >
                ACT
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-24 px-2 py-3.5 border-[#F0F0F0] batch-table-header"
                colSpan={2}
              >
                SAT
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-24 px-2 py-3.5 border-[#F0F0F0] batch-table-header"
                colSpan={2}
              >
                Courses
              </th>
              <th className="text-sm text-[#535862] font-medium text-center border w-24 px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Status
              </th>
              <th className="text-sm text-[#535862] font-medium text-center border px-5 py-3.5 border-[#F0F0F0] batch-table-header">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="border border-[#F0F0F0]">
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="px-5 py-2 batch-table-cell">
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                    </div>
                  </td>
                </tr>
              ))
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item, index) => {
                const start_date = new Date(item.start_date).toLocaleDateString(
                  'en-US'
                )
                const end_date = new Date(item.end_date).toLocaleDateString(
                  'en-US'
                )
                return (
                  <tr key={item._id} className="border border-[#F0F0F0]">
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                      {(index + 1).toString().padStart(2, '0')}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] font-semibold batch-table-cell">
                      {item.batch_code}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] font-semibold batch-table-cell">
                      {`${new Date(item.start_date).toLocaleDateString(
                        'en-US',
                        { year: '2-digit', month: '2-digit', day: '2-digit' }
                      )} - ${new Date(item.end_date).toLocaleDateString(
                        'en-US',
                        { year: '2-digit', month: '2-digit', day: '2-digit' }
                      )}`}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                      {item.batch_title}
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                      <Link to={`/students_batches?batch_id=${item._id}`}>
                        <div className="px-3 py-1 bg-[#DE3C7D] text-white rounded-md hover:bg-[#C62D6D] transition-colors">
                          {item.student_count}
                        </div>
                      </Link>
                    </td>
                    <td className="text-sm text-[#030303] text-center border w-14 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <Link to={`/acttests_batches?batch_id=${item._id}`}>
                        <ScoreDisplay score={item.act_count} type="ACT" />
                      </Link>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <button
                        className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                        title="Assign ACT Tests"
                        onClick={() => navigate(`/assignacttests/${item._id}`)}
                      >
                        <span className="text-[#535862] text-lg font-bold">
                          +
                        </span>
                      </button>
                    </td>
                    <td className="text-sm text-[#030303] text-center border w-14 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <Link to={`/sattests_batches?batch_id=${item._id}`}>
                        <ScoreDisplay score={item.sat_count} type="SAT" />
                      </Link>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <button
                        className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                        title="Assign SAT Tests"
                        onClick={() => navigate(`/assignsattests/${item._id}`)}
                      >
                        <span className="text-[#535862] text-lg font-bold">
                          +
                        </span>
                      </button>
                    </td>
                    <td className="text-sm text-[#030303] text-center border w-14 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <Link to={`/assigncoursesbatch/${item._id}`}>
                        <div
                          className="text-white self-stretch min-h-8 w-8 h-8 my-auto rounded-[10px] flex items-center justify-center text-sm font-medium whitespace-nowrap leading-[1.2] cursor-pointer hover:opacity-90 transition-opacity mx-auto"
                          style={{ backgroundColor: '#DE3C7D' }}
                        >
                          {item.course_count ?? 0}
                        </div>
                      </Link>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0] batch-table-cell">
                      <button
                        className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                        title="Assign Courses"
                        onClick={() => navigate(`/assigncoursesbatch/${item._id}`)}
                      >
                        <span className="text-[#535862] text-lg font-bold">
                          +
                        </span>
                      </button>
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0] batch-table-cell">
                      <div className="flex justify-center">
                        <StatusToggle
                          status={item.status}
                          onChange={() =>
                            handleStatusToggle(item._id, item.status)
                          }
                        />
                      </div>
                    </td>
                    <td className="px-5 py-2 batch-table-cell">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                          title="Assign Tests"
                          onClick={() => navigate(`/assigntests/${item._id}`)}
                        >
                          <span className="text-[#535862] text-lg font-bold">
                            +
                          </span>
                        </button>
                        <button
                          className="border bg-neutral-50 p-2 rounded-[10px] border-[#F0F0F0] hover:bg-neutral-100 transition-colors"
                          title="Edit"
                          onClick={() => handleOpenEditModal(item._id)}
                        >
                          <span className="text-[#535862]">
                            <EditIcon />
                          </span>
                        </button>
                        <button
                          className="border bg-[rgba(233,0,4,0.10)] p-2 rounded-[10px] border-[rgba(233,0,4,0.20)] hover:bg-[rgba(233,0,4,0.15)] transition-colors"
                          title="Delete"
                          onClick={() => handleDeleteClick(item._id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={13} className="text-center py-8 text-[#535862]">
                  No batches found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {filteredData.length > 0 && (
          <div className="flex justify-between items-center mt-4 px-4">
            <div className="text-sm text-[#535862]">
              Showing {filteredData.length === 0 ? 0 : indexOfFirstEntry + 1} to{' '}
              {Math.min(indexOfLastEntry, filteredData.length)} of{' '}
              {filteredData.length} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-3 py-1 border border-[#F0F0F0] rounded-md ${
                    currentPage === index + 1 ? 'bg-[#DE3C7D] text-white' : ''
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ToastContainer />

      {/* Status Toggle Modal */}
      <ConfirmModal
        open={statusModalOpen}
        title={
          statusModalAction?.type === 'block'
            ? 'Deactivate Batch'
            : 'Activate Batch'
        }
        message={
          statusModalAction?.type === 'block'
            ? 'Are you sure you want to deactivate this batch?'
            : 'Are you sure you want to activate this batch?'
        }
        onCancel={() => {
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
        onConfirm={async () => {
          if (statusModalAction) {
            if (statusModalAction.type === 'block') {
              await blockBatch(statusModalAction.id)
            } else {
              await unblockBatch(statusModalAction.id)
            }
          }
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
      />

      {/* Delete Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Batch"
        message="Are you sure you want to delete this batch? This action cannot be undone."
        onCancel={() => {
          setDeleteModalOpen(false)
          setBatchToDelete(null)
        }}
        onConfirm={async () => {
          if (batchToDelete) {
            await deleteBatch(batchToDelete)
          }
          setDeleteModalOpen(false)
          setBatchToDelete(null)
        }}
      />

      <Modal open={addModalOpen} onClose={handleCloseAddModal}>
        <AddBatch onClose={handleCloseAddModal} />
      </Modal>

      <Modal open={editModalOpen} onClose={handleCloseEditModal}>
        <EditBatch onClose={handleCloseEditModal} id={selectedBatchId} />
      </Modal>

      <style>{`
        .batch-table-cell {
          color: #030303;
          font-family: 'Basis Grotesque Arabic Pro', Arial, sans-serif;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 120%;
        }
        .batch-table-header {
          color: #535862;
          font-family: 'Basis Grotesque Arabic Pro', Arial, sans-serif;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 120%;
        }
      `}</style>
    </section>
  )
}

export default BatchList
